// localStorage-backed capability override storage.
//
// Slice RP-2b-i of docs/planning/firefly_role_context_presentation.md
// §10. Mirrors the assist-level pattern in
// `src/lib/activityDock/assistLevel.ts`: a small storage module + a
// subscriber set + cross-tab `storage` event glue, so the eventual
// Settings UI hook (RP-2b-ii) is a thin presentational layer over
// this module.
//
// What this file stores: only *explicit* user overrides for each
// capability. A capability that's not in the stored object inherits
// its **role-derived default** (computed by `capabilityResolution.ts`,
// not here). This three-state model — explicit-on / explicit-off /
// no-preference — is necessary because:
//
//   - A brand-new sales rep should see ads tasks by default
//     (role-derived `is_ads_runner = true`). Storing only an explicit
//     override means we don't have to write a value to localStorage
//     on first login — the default already lives in the resolution
//     helper.
//
//   - An owner-only user should NOT see ads tasks by default
//     (role-derived `is_ads_runner = false`). Same mechanism — no
//     write needed.
//
//   - A sales rep who actually doesn't run ads can flip an explicit
//     `false` override so the default is no longer used (this slice
//     ships the storage; the UI lands in RP-2b-ii).
//
// Wire format: a JSON object `{ [capability]: boolean }` under the
// single localStorage key below. Unknown keys (e.g. a future
// capability that doesn't exist in the running build) are tolerated
// during parse — they're simply ignored by `parseCapabilityOverrides`.
// Stored booleans for capabilities that DO exist in the current
// build are kept verbatim.

import { type Capability } from "./capabilities.js";
import {
  parseCapabilityOverrides,
  type CapabilityOverrides,
} from "./capabilityOverrides.js";

export const CAPABILITY_OVERRIDES_STORAGE_KEY = "bb-setting-user-capabilities";

export type { CapabilityOverrides };
export { parseCapabilityOverrides };

// -- Storage glue --------------------------------------------------------

type Listener = (next: CapabilityOverrides) => void;
const listeners = new Set<Listener>();

function readStoredOverrides(): CapabilityOverrides {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(CAPABILITY_OVERRIDES_STORAGE_KEY);
    if (!raw) return {};
    return parseCapabilityOverrides(JSON.parse(raw));
  } catch {
    return {};
  }
}

function writeStoredOverrides(next: CapabilityOverrides): void {
  if (typeof localStorage === "undefined") return;
  try {
    // Serialize only known + boolean entries to keep the wire format
    // self-cleaning. Empty object is fine — we don't bother removing
    // the key when overrides clear, since `parseCapabilityOverrides`
    // treats `{}` and a missing key identically.
    const sanitized = parseCapabilityOverrides(next);
    localStorage.setItem(
      CAPABILITY_OVERRIDES_STORAGE_KEY,
      JSON.stringify(sanitized),
    );
  } catch {
    // Quota / private-mode failures: skip persistence but still
    // notify subscribers so the in-tab UI stays consistent with the
    // user's intent for this session.
  }
}

/** Returns the current overrides snapshot. SSR-safe (`{}` fallback). */
export function getCapabilityOverrides(): CapabilityOverrides {
  return readStoredOverrides();
}

/**
 * Set or clear the override for one capability. `null` clears the
 * override (no preference; fall back to role-derived default);
 * `true` / `false` set an explicit value.
 */
export function setCapabilityOverride(
  capability: Capability,
  value: boolean | null,
): void {
  const current = readStoredOverrides();
  let next: CapabilityOverrides;
  if (value === null) {
    next = { ...current };
    delete next[capability];
  } else {
    next = { ...current, [capability]: value };
  }
  writeStoredOverrides(next);
  for (const listener of listeners) listener(next);
}

/**
 * Subscribe to in-tab writes + cross-tab `storage` events. Returns
 * an unsubscribe function. The listener receives the full sanitized
 * `CapabilityOverrides` snapshot on every change.
 */
export function subscribeCapabilityOverrides(listener: Listener): () => void {
  listeners.add(listener);
  let storageHandler: ((e: StorageEvent) => void) | null = null;
  if (typeof window !== "undefined") {
    storageHandler = (e) => {
      if (e.key !== CAPABILITY_OVERRIDES_STORAGE_KEY) return;
      let parsed: CapabilityOverrides = {};
      try {
        parsed = parseCapabilityOverrides(
          e.newValue ? JSON.parse(e.newValue) : null,
        );
      } catch {
        parsed = {};
      }
      listener(parsed);
    };
    window.addEventListener("storage", storageHandler);
  }
  return () => {
    listeners.delete(listener);
    if (storageHandler && typeof window !== "undefined") {
      window.removeEventListener("storage", storageHandler);
    }
  };
}
